import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import BaseWidget from '../BaseWidget';
import useWidgetData from '../hooks/useWidgetData';
import { useFilterStoreWithMomentDates } from '../../../store/filterStore.js';
import { transformWidgetData } from '../widgetConfigSchema';
import {
	AreaChart,
	Area,
	XAxis,
	YAxis,
	ResponsiveContainer,
	Tooltip as RechartsTooltip,
} from "recharts";
import { formatValue } from "../../Utils.js";
import moment from "moment";
import dayjs from "dayjs";
import SmartRechartsTooltip from "../../chart-components/SmartRechartsTooltip";

function sortData(data) {
	if (data !== null && data !== undefined && data.length > 0) {
		return [...data].sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
	}
	return data;
}

function formatYAxis(value, format) {
	return formatValue(value, format, false);
}

function formatXAxis(value, format) {
	return dayjs(value).format("DD-MM-YYYY");
}

/**
 * Get human-readable title for a measure code
 * @param {string} measure - The measure code (e.g., 'cpu_utilization')
 * @returns {string} - Human-readable title (e.g., 'CPU Utilization')
 */
function getMeasureTitle(measure) {
	switch (measure) {
		case "cpu_utilization":
			return "CPU Utilization";
		case "memory_utilization":
			return "Memory Utilization";
		case "storage_utilization":
			return "Storage Utilization";
		default:
			return "Utilization";
	}
}

// Custom tooltip content component matching AreaChartComponent_v2 style
const CustomTooltipContent = ({ tooltipData, measure }) => {
	if (!tooltipData) return null;

	const { startTime, endTime, value } = tooltipData;
	
	const title = getMeasureTitle(measure);

	const startMoment = moment(startTime);
	const endMoment = moment(endTime);
	const startFormatted = startMoment.format("dddd, MMMM D");
	const timePeriod = startMoment.format("HH:mm") + " - " + endMoment.format("HH:mm");

	return (
		<>
			<div className="flex flex-col mb-2 min-w-[240px]">
				<div className="flex flex-row">
					<p className="flex-1 label mr-1 text-text-2 whitespace-nowrap">
						{startFormatted}
					</p>
				</div>
				<div className="flex flex-row">
					<p className="flex-1 label mr-4 text-text-2">
						{timePeriod}
					</p>
				</div>
			</div>
			
			<div className="value flex flex-col items-start mb-2">
				{/* Series name */}
				<span className="flex-1 mr-4 mb-1 text-lg text-text">{title}</span>

				{/* Value */}
				<span className="w-[150px] text-xl font-semibold mr-3 flex items-baseline">
					{formatValue(value, "percent", false, 1)}
				</span>
			</div>
		</>
	);
};

/**
 * ResourceUtilizationChart - Copy of UtilizationChartComponent for widget use
 */
const ResourceUtilizationChart = (props) => {
	const { data, title, measure, format, color } = props;

	const chartColor = color || "blue";
	const strokeColor = chartColor === "blue" ? "hsl(var(--chart-1))" : "hsl(var(--chart-3))";

	// Sort data once before rendering
	const sortedData = useMemo(() => sortData(data), [data]);

	// Memoize buildTooltipData to prevent recreation on every render
	const buildTooltipData = useCallback((tooltipProps) => {
		if (!tooltipProps || !tooltipProps.payload || tooltipProps.payload.length === 0) {
			return null;
		}
		const p = tooltipProps.payload[0];
		if (!p || !p.payload) return null;
		const row = p.payload;
		const value = row[measure];
		if (value === undefined || value === null) return null;
		return {
			startTime: row.start_time,
			endTime: row.end_time,
			value: value
		};
	}, [measure]);

	// Memoize renderContent to prevent recreation on every render
	const renderTooltipContent = useCallback(({ data: tooltipData }) => (
		<CustomTooltipContent tooltipData={tooltipData} measure={measure} />
	), [measure]);

	if (!data || !Array.isArray(data) || data.length === 0) {
		return (
			<div className="h-full flex items-center justify-center">
				<div className="text-text-2 text-sm text-center">
					No utilization data available
				</div>
			</div>
		);
	}

	return (
		<div 
			className="h-full w-full flex flex-col" 
			style={{ height: '100%', position: 'relative' }}
		>
			<div className="flex-1 w-full" style={{ minHeight: 0, position: 'relative' }}>
				<div style={{ position: 'absolute', top: 40, left: 0, right: 0, bottom: 0 }}>
					<ResponsiveContainer width="100%" height="100%">
					<AreaChart
						id={title}
						data={sortedData}
						margin={{
							top: 10,
							left: 0,
							bottom: 20,
							right: 10,
						}}
					>
				<defs>
					<linearGradient id="blue" x1="0" y1="0" x2="0" y2="1">
						<stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.25}/>
						<stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0}/>
					</linearGradient>
					<linearGradient id="green" x1="0" y1="0" x2="0" y2="1">
						<stop offset="5%" stopColor="hsl(var(--chart-3))" stopOpacity={0.25}/>
						<stop offset="95%" stopColor="hsl(var(--chart-3))" stopOpacity={0}/>
					</linearGradient>
				</defs>

				<XAxis 
					dataKey="start_time" 
					tick={{ fontSize: 12 }} 
					tickFormatter={(value) => formatXAxis(value, format || "percent")} 
				/>
				<YAxis 
					tick={{ fontSize: 12 }} 
					tickFormatter={(value) => formatYAxis(value, format || "percent")} 
				/>
				<RechartsTooltip
					cursor={{ fill: "#F4F4F4" }}
					content={SmartRechartsTooltip.createContentCallback({
						buildTooltipData,
						renderContent: renderTooltipContent,
						estimatedWidth: 280,
						estimatedHeight: 200,
						offsetX: 50,
						offsetY: -175,
					})}
				/>

				<Area
					fillOpacity={1} 
					fill={chartColor === "blue" ? "url(#blue)" : "url(#green)"}
					type="monotone"
					dot={false}
					stroke={strokeColor}
					strokeWidth={2}
					dataKey={measure}
					isAnimationActive={false}
				/>
					</AreaChart>
					</ResponsiveContainer>
				</div>
			</div>
		</div>
	);
};

/**
 * ResourceUtilization Widget - Shows utilization metrics for a selected resource
 */
const ResourceUtilizationWidget = ({ 
  widgetId, 
  config, 
  filters = [], 
  onRemove, 
  isEditMode = false
}) => {
  const { startDate, endDate } = useFilterStoreWithMomentDates();
  
  // Stable filters to avoid re-fetch loops
  const stableFilters = useMemo(() => filters || [], [filters]);

  // Build config with updated dates merged into the request body
  const finalConfig = useMemo(() => {
    const dataSource = config?.dataSource || {};
    const baseBody = dataSource.body || {};
    
    // Only add dates if both are present
    const bodyWithDates = (startDate && endDate) ? {
      ...baseBody,
      start_date: startDate.format("YYYY-MM-DD"),
      end_date: endDate.format("YYYY-MM-DD")
    } : baseBody;

    return {
      ...config,
      dataSource: {
        ...dataSource,
        method: dataSource.method || 'POST',
        body: bodyWithDates
      }
    };
  }, [config, startDate, endDate]);

  const { data, isLoading, error } = useWidgetData(finalConfig, stableFilters);

  // Transform data if it comes from API
  const widgetData = data ? transformWidgetData('resourceutilization', data) : null;

  // Get selected resource name and measure
  const selectedResourceName = config?.selectedResourceName || 'No resource selected';
  const measure = config?.measure || 'cpu_utilization';
  
  // Get measure title and concatenate with resource name
  const chartTitle = useMemo(() => {
    const measureTitle = getMeasureTitle(measure);
    
    // Concatenate resource name with measure title
    if (selectedResourceName && selectedResourceName !== 'No resource selected') {
      return `${selectedResourceName} - ${measureTitle}`;
    }
    return measureTitle;
  }, [measure, selectedResourceName]);

  // Check if resource is selected
  const hasResourceSelected = Boolean(config?.selectedResourceId && config.selectedResourceId !== '');

  return (
    <BaseWidget
      widgetId={widgetId}
      title={config.title}
      isLoading={isLoading}
      error={error}
      onRemove={onRemove}
      isEditMode={isEditMode}
    >
      <div 
        className="h-full flex flex-col bg-card rounded-card border border-card-bd p-4"
        style={{ minHeight: '200px', height: '100%', overflow: 'hidden', position: 'relative' }}
      >
        {hasResourceSelected ? (
          <>
            {/* Chart Title with Resource Name - matching Resource Changes table style */}
            {chartTitle && (
              <div className="absolute top-2 left-2 z-10 p-2 pointer-events-none">
                <div className="text-text text-lg font-semibold widget-title" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {chartTitle}
                </div>
              </div>
            )}
            
            {/* Chart */}
            {widgetData && widgetData.data && widgetData.data.length > 0 ? (
              <div className={`flex-1 flex flex-col min-h-0 ${chartTitle ? 'pt-2' : ''}`} style={{ minHeight: 0, position: 'relative' }}>
                <ResourceUtilizationChart
                  data={widgetData.data}
                  title={chartTitle}
                  measure={measure}
                  format="percent"
                  color="blue"
                />
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <p className="text-text-2 text-sm">
                  {isLoading ? 'Loading utilization data...' : 'No utilization data available for the selected period'}
                </p>
              </div>
            )}
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-text-2 text-sm">
              Please select a resource in the widget configuration
            </p>
          </div>
        )}
      </div>
    </BaseWidget>
  );
};

CustomTooltipContent.propTypes = {
  tooltipData: PropTypes.shape({
    startTime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    endTime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    value: PropTypes.number
  }),
  measure: PropTypes.string
};

ResourceUtilizationChart.propTypes = {
  data: PropTypes.array,
  title: PropTypes.string,
  measure: PropTypes.string,
  format: PropTypes.string,
  color: PropTypes.string
};

ResourceUtilizationWidget.propTypes = {
  widgetId: PropTypes.string.isRequired,
  config: PropTypes.shape({
    title: PropTypes.string.isRequired,
    selectedResourceId: PropTypes.string,
    selectedResourceName: PropTypes.string,
    measure: PropTypes.oneOf(['cpu_utilization', 'memory_utilization', 'storage_utilization']),
    dataSource: PropTypes.shape({
      type: PropTypes.oneOf(['static', 'api']).isRequired,
      data: PropTypes.any,
      endpoint: PropTypes.string,
      method: PropTypes.string,
      body: PropTypes.object,
      transform: PropTypes.func
    }).isRequired
  }).isRequired,
  filters: PropTypes.array,
  onRemove: PropTypes.func,
  isEditMode: PropTypes.bool
};

export default ResourceUtilizationWidget;

